import { ChangeEvent, Dispatch, SetStateAction } from 'react';
import RangeSlider from 'react-range-slider-input';
import Select from 'react-select';
import info from '../../../../../assets/images/icons/info.svg';
import { CustomStyle } from '../../../../../components/CustomSelectStyle';
import FilterRadioButton from '../../../../../components/FilterRadioButtons';
import { FilterRadioButtonsContainer } from '../../../../../components/FilterRadioButtonsContainer';
import FormGroup from '../../../../../components/FormGroup';
import Input from '../../../../../components/Input';
import OpacityAnimation from '../../../../../components/OpacityAnimation';
import { GetErrorMessageByFieldNameType } from '../../../../../hooks/useErrors';
import useGetGeneralAppData from '../../../../../hooks/useGetGeneralAppData';
import { TransfersBehaviorType } from '../../../../Company/Settings/types';
import { AsideContainer, Group, Container as StyledContainer } from '../styles';
import { ConditionalInputGroup, RangeSliderContainer } from './styles';

interface GeneralDataCardI {
  getErrorMessageByFieldName: GetErrorMessageByFieldNameType;
  workplaceCode: string;
  handleWorkplaceCodeChange: (event: ChangeEvent<HTMLInputElement>) => void;
  name: string;
  handleNameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  hasAffreightment: boolean;
  setHasAffreightment: Dispatch<SetStateAction<boolean>>;
  operator: { value: string, label: string };
  operatorsOptions: { value: string, label: string }[];
  setOperator: Dispatch<SetStateAction<{ value: string, label: string }>>;
  usesVtAndAffreightment: boolean;
  setUsesVtAndAffreightment: Dispatch<SetStateAction<boolean>>;
  checkIfWorkplaceCodeAlreadyExists: () => Promise<void>;
  compareWithDesiredVtValue: boolean;
  desiredVtValue: string;
  handleCompareWithDesiredVtValueChange: (willCompare: boolean) => void;
  handleDesiredVtValueChange: (event: ChangeEvent<HTMLInputElement>) => void;
  selectedWalkDistance: number[];
  setSelectedWalkDistance: Dispatch<SetStateAction<number[]>>;
  handleWalkDistanceSelection: (range: number[]) => void;
  sameWalkDistanceFromCompany: boolean;
  handleSameWalkDistanceFromCompanyChange: (willUseCompanyValue: boolean) => void;
  sameDesiredVtValueFromCompany: boolean;
  handleSameDesiredVtValueFromCompanyChange: (willUseCompanyValue: boolean) => void;
  kindOfVtSpTrans: 'common' | 'vt';
  setKindOfVtSpTrans: Dispatch<SetStateAction<'common' | 'vt'>>;
  integratesWithRiocard: boolean;
  setIntegratesWithRiocard: Dispatch<SetStateAction<boolean>>;
  hasKmLimit: boolean;
  setHasKmLimit: Dispatch<SetStateAction<boolean>>;
  selectedKmLimit: number[];
  setSelectedKmLimit: Dispatch<SetStateAction<number[]>>;
  handleKmLimitSelection: (range: number[]) => void;
  transfersBehavior: TransfersBehaviorType;
  setTransfersBehavior: Dispatch<SetStateAction<TransfersBehaviorType>>;
  companyTransfersBehavior: TransfersBehaviorType;
  sameTransfersBehaviorFromCompany: boolean;
  handleSameTransfersBehaviorFromCompanyChange: (willUseCompanyValue: boolean) => void;
}

export default function GeneralDataCard({
  getErrorMessageByFieldName,
  workplaceCode,
  handleWorkplaceCodeChange,
  name,
  handleNameChange,
  hasAffreightment,
  setHasAffreightment,
  operator,
  operatorsOptions,
  setOperator,
  usesVtAndAffreightment,
  setUsesVtAndAffreightment,
  checkIfWorkplaceCodeAlreadyExists,
  selectedWalkDistance,
  handleWalkDistanceSelection,
  setSelectedWalkDistance,
  desiredVtValue,
  handleDesiredVtValueChange,
  sameWalkDistanceFromCompany,
  handleSameWalkDistanceFromCompanyChange,
  sameDesiredVtValueFromCompany,
  handleSameDesiredVtValueFromCompanyChange,
  kindOfVtSpTrans,
  setKindOfVtSpTrans,
  integratesWithRiocard,
  setIntegratesWithRiocard,
  hasKmLimit,
  setHasKmLimit,
  selectedKmLimit,
  handleKmLimitSelection,
  setSelectedKmLimit,
  transfersBehavior,
  setTransfersBehavior,
  companyTransfersBehavior,
  sameTransfersBehaviorFromCompany,
  handleSameTransfersBehaviorFromCompanyChange,
}: GeneralDataCardI) {
  const transfersBehaviorLabels: { [key in TransfersBehaviorType]: string } = {
    'default': 'Padrão',
    'unlimited': 'Permitir baldeações ilimitadas',
    'minimun': 'Mínimo de baldeações possíveis',
  };

  function handleTransfersBehaviorChange(value: TransfersBehaviorType) {
    setTransfersBehavior(value);
    if (value === companyTransfersBehavior) {
      handleSameTransfersBehaviorFromCompanyChange(true);
    }
  }
  const { permissions } = useGetGeneralAppData();
  const settingsPagePermissions = permissions.find((x) => x.codPag === 2);
  const canNavToSettings = settingsPagePermissions?.vizualizar;



  return (
    <StyledContainer>
      <div className="card-title">
        Dados Gerais
      </div>

      <AsideContainer>
        <Group>
          <div className="title">
            Código cliente
          </div>
          <FormGroup error={getErrorMessageByFieldName('workplaceCode')}>
            <Input
              value={workplaceCode}
              onChange={handleWorkplaceCodeChange}
              error={getErrorMessageByFieldName('workplaceCode')}
              placeholder="Defina o código identificador do local de trabalho"
              onBlur={() => checkIfWorkplaceCodeAlreadyExists()}
              maxLength={25}
            />
          </FormGroup>
        </Group>
        <Group>
          <div className="title">
            Denominação
          </div>
          <FormGroup error={getErrorMessageByFieldName('name')}>
            <Input
              value={name}
              onChange={handleNameChange}
              error={getErrorMessageByFieldName('name')}
              placeholder="Nome do local de trabalho"
              maxLength={40}
            />
          </FormGroup>
        </Group>
      </AsideContainer>

      {canNavToSettings && (
        <>
          <Group>
            <div className="title">
          Utiliza fretamento?
            </div>
            <FilterRadioButtonsContainer>
              <FilterRadioButton
                selected={hasAffreightment}
                onClick={() => setHasAffreightment(true)}
              >
            Sim
              </FilterRadioButton>
              <FilterRadioButton
                selected={!hasAffreightment}
                onClick={() => setHasAffreightment(false)}
              >
            Não
              </FilterRadioButton>
            </FilterRadioButtonsContainer>
          </Group>

          {hasAffreightment && (
            <AsideContainer>
              <Group>
                <div className="title">
              Operadora de fretamento
                </div>
                <FormGroup>
                  <Select
                    value={{ value: operator?.value, label: operator?.label }}
                    options={operatorsOptions}
                    onChange={(opt) => {
                      setOperator({ value: opt!.value, label: opt!.label });
                    }}
                    styles={CustomStyle}
                    classNamePrefix="react-select"
                    className="react-select-container"
                  />
                </FormGroup>
              </Group>

              <Group>
                <div className="title">
              Combina fretamento com VT?
                </div>
                <FilterRadioButtonsContainer>
                  <FilterRadioButton
                    selected={usesVtAndAffreightment}
                    onClick={() => setUsesVtAndAffreightment(true)}
                  >
                Sim
                  </FilterRadioButton>
                  <FilterRadioButton
                    selected={!usesVtAndAffreightment}
                    onClick={() => setUsesVtAndAffreightment(false)}
                  >
                Não
                  </FilterRadioButton>
                </FilterRadioButtonsContainer>
              </Group>
            </AsideContainer>
          )}

          <Group>
            <div className="title">
              <div>
            Limite de distância caminhada
                {' '}
                <img src={info} alt="info" title="Define qual a distância máxima caminhada permitida nas etapas do trajeto. Quanto menor a distância, maior a possibilidade de não ser encontrado um resultado, e maior as chances do resultado ter um valor mais elevado em relação a uma distância caminhada maior." />
              </div>
            </div>
            <ConditionalInputGroup>
              <FilterRadioButtonsContainer notOverflow>
                <FilterRadioButton selected={sameWalkDistanceFromCompany} onClick={() => handleSameWalkDistanceFromCompanyChange(true)}>
                Manter mesma configuração da empresa
                </FilterRadioButton>
                <FilterRadioButton selected={!sameWalkDistanceFromCompany} onClick={() => handleSameWalkDistanceFromCompanyChange(false)}>
                  Personalizar para este local
                </FilterRadioButton>
              </FilterRadioButtonsContainer>

              {!sameWalkDistanceFromCompany && (
                <RangeSliderContainer>
                  <header>
                    {` ${selectedWalkDistance[1]}m`}
                  </header>
                  <RangeSlider
                    min={500}
                    max={2000}
                    value={selectedWalkDistance}
                    onThumbDragEnd={() => handleWalkDistanceSelection(selectedWalkDistance)}
                    onInput={(evt: number[]) => setSelectedWalkDistance(evt)}
                    className="single-thumb"
                    thumbsDisabled={[true, false]}
                    rangeSlideDisabled
                  />
                </RangeSliderContainer>
              )}
            </ConditionalInputGroup>
          </Group>

          <Group>
            <div className="title">
              <div>
            Valor máximo de VT ideal
                {' '}
                <img src={info} alt="info" title="Irá fazer uma comparação do valor total da roteirização com o valor informado, caso o valor roteirizado seja maior que o valor definido aqui, considerará a consulta com status de Roteirizado Acima do Teto" />
              </div>
            </div>
            <ConditionalInputGroup>
              <FilterRadioButtonsContainer notOverflow>
                <FilterRadioButton selected={sameDesiredVtValueFromCompany} onClick={() => handleSameDesiredVtValueFromCompanyChange(true)}>
                Manter mesma configuração da empresa
                </FilterRadioButton>
                <FilterRadioButton
                  selected={!sameDesiredVtValueFromCompany}
                  onClick={() => {
                    handleSameDesiredVtValueFromCompanyChange(false);
                  }}
                >
                  Personalizar para este local
                </FilterRadioButton>
              </FilterRadioButtonsContainer>

              {(!sameDesiredVtValueFromCompany) && (
                <OpacityAnimation>
                  <FormGroup error={getErrorMessageByFieldName('desiredVtValue')}>
                    <Input
                      value={desiredVtValue}
                      onChange={handleDesiredVtValueChange}
                      error={getErrorMessageByFieldName('desiredVtValue')}
                      placeholder="R$ XX,XX"
                      maxLength={8}
                    />
                  </FormGroup>
                </OpacityAnimation>
              )}

            </ConditionalInputGroup>
          </Group>

          <Group>
            <div className="title">
              <div>
            Para a SPTRANS, realiza a recarga como Vale Comum, ou VT?
                {' '}
                <img src={info} alt="info" title="O VT possui um valor ligeiramente mais alto que o vale comum, por isso algumas empresas optam por realizar a recarga como vale comum, ou reembolsar o colaborador em dinheiro." />
              </div>
            </div>
            <FilterRadioButtonsContainer>
              <FilterRadioButton selected={kindOfVtSpTrans === 'vt'} onClick={() => setKindOfVtSpTrans('vt')}>
                  VT
              </FilterRadioButton>
              <FilterRadioButton selected={kindOfVtSpTrans === 'common'} onClick={() => setKindOfVtSpTrans('common')}>
                  Comum (mediante liminar da justiça)
              </FilterRadioButton>
            </FilterRadioButtonsContainer>
          </Group>

          <Group>
            <div className="title">
              <div>
            Realiza Integrações?
                {' '}
                <img src={info} alt="info" title="Define se as roteirizações devem acatar os valores realizando as integrações ou não" />
              </div>
            </div>
            <FilterRadioButtonsContainer>
              <FilterRadioButton selected={integratesWithRiocard} onClick={() => setIntegratesWithRiocard(true)}>
                  Sim
              </FilterRadioButton>
              <FilterRadioButton selected={!integratesWithRiocard} onClick={() => setIntegratesWithRiocard(false)}>
                  Não
              </FilterRadioButton>
            </FilterRadioButtonsContainer>
          </Group>

          <Group>
            <div className="title">
              <div>
            Comportamento referente à baldeações
                {' '}
                <img src={info} alt="info" title="O comportamento padrão prioriza até 2 baldeações, um bom equilíbrio entre tempo/distância, quantidade de baldeações e custo. Caso seja definido baldeações ilimitadas, os resultados apresentados terão o menor tempo/distância menores, porém podem ter muitas baldeações, e, consequentemente um custo maior. O mínimo de baldeações possíveis poderá ocasionar em um tempo/distância muito longos, porém com a menor quantidade de baldeações possíveis, e, consequentemente, um custo menor" />
              </div>
            </div>
            <FilterRadioButtonsContainer notOverflow>
              <FilterRadioButton selected={sameTransfersBehaviorFromCompany} onClick={() => handleSameTransfersBehaviorFromCompanyChange(true)}>
                {`Manter mesma configuração da empresa (${transfersBehaviorLabels[companyTransfersBehavior]})`}
              </FilterRadioButton>
              <FilterRadioButton selected={!sameTransfersBehaviorFromCompany} onClick={() => handleSameTransfersBehaviorFromCompanyChange(false)}>
                Personalizar para este local
              </FilterRadioButton>
            </FilterRadioButtonsContainer>

            {!sameTransfersBehaviorFromCompany && (
              <OpacityAnimation>
                <FilterRadioButtonsContainer>
                  <FilterRadioButton selected={transfersBehavior === 'default'} onClick={() => handleTransfersBehaviorChange('default')}>
                    Padrão
                  </FilterRadioButton>
                  <FilterRadioButton selected={transfersBehavior === 'unlimited'} onClick={() => handleTransfersBehaviorChange('unlimited')}>
                    Permitir baldeações ilimitadas
                  </FilterRadioButton>
                  <FilterRadioButton selected={transfersBehavior === 'minimun'} onClick={() => handleTransfersBehaviorChange('minimun')}>
                    Mínimo de baldeações possíveis
                  </FilterRadioButton>
                </FilterRadioButtonsContainer>
              </OpacityAnimation>
            )}
          </Group>

          <Group>
            <div className="title">
              <div>
            Utilizar limitador de Km?
                {' '}
                <img src={info} alt="info" title="Apresentará o resultado em que a distância total do trajeto tenha até a quilometragem definida neste campo, para não levar em consideração trajetos muito longos, que podem ser mais baratos, porém muito cansativo para o colaborador. Caso nenhum resultado seja encontrado conforme a limitação de quilometragem, o trajeto mais curto possível será mostrado." />
              </div>
            </div>
            <ConditionalInputGroup>
              <FilterRadioButtonsContainer notOverflow>
                <FilterRadioButton selected={hasKmLimit} onClick={() => setHasKmLimit(true)}>
                  Sim
                </FilterRadioButton>
                <FilterRadioButton selected={!hasKmLimit} onClick={() => setHasKmLimit(false)}>
                  Não
                </FilterRadioButton>
              </FilterRadioButtonsContainer>

              {hasKmLimit && (
                <OpacityAnimation>
                  <RangeSliderContainer>
                    <header>
                      {` ${selectedKmLimit[1]}km`}
                    </header>
                    <RangeSlider
                      min={5}
                      max={50}
                      value={selectedKmLimit}
                      onThumbDragEnd={() => handleKmLimitSelection(selectedKmLimit)}
                      onInput={(evt: number[]) => setSelectedKmLimit(evt)}
                      className="single-thumb"
                      thumbsDisabled={[true, false]}
                      rangeSlideDisabled
                    />
                  </RangeSliderContainer>
                </OpacityAnimation>
              )}
            </ConditionalInputGroup>
          </Group>
        </>
      )}
    </StyledContainer>
  );
}
